// Dedup helper para GET requests idempotentes que varios componentes piden
// casi al mismo tiempo durante la carga inicial (login/onboarding). Colapsa
// llamadas concurrentes al MISMO url (incluyendo query string, a diferencia
// del dedup de sync-status en App.js que ignora la query) en un solo request
// de red: reusa la promesa en vuelo y, una vez resuelta, reusa el Response
// clonado durante una ventana corta.
//
// Por que por URL completa (no por pathname): los endpoints que usa este
// helper (/revenue-finder/summary, /shopify/status, /klaviyo/status) siempre
// se piden con el mismo query (?company=X) desde los distintos call sites, asi
// que no hay riesgo de mezclar respuestas de distinto company. Si en el futuro
// se agrega un call site con query distinta al mismo recurso, revisar esto.
//
// No reemplaza cache de mas largo plazo (SWR/react-query) — es intencionalmente
// una ventana corta solo para colapsar la rafaga de fetches simultaneos del
// primer render, no para evitar refrescos reales.
//
// OJO (revision independiente): la ventana NO es un TTL fijo desde el primer
// fetch — es una ventana DESLIZANTE. Cada vez que se resuelve la promesa (o
// llega un request dentro de la ventana vigente) se reescribe `ts` a
// Date.now(), asi que una racha de requests separados por menos de windowMs
// entre si puede mantener la entrada "viva" mas alla de los 3s nominales
// desde el primer request. Para el uso actual (colapsar la rafaga del primer
// render) es el comportamiento deseado; no asumir un vencimiento exacto a
// los 3s si se reusa este helper en otro contexto.
//
// TODO (revision independiente): _inflight nunca se poda — cada URL unica
// vista en la sesion queda como entrada indefinidamente (nunca se borra tras
// expirar la ventana, salvo el caso de error que hace .delete()). Hoy es bajo
// riesgo: los call sites actuales piden siempre la misma URL fija por
// company+endpoint, asi que el Map se mantiene chico. Si este helper se
// reusa con URLs muy variables (timestamps, filtros dinamicos en query, IDs
// unicos por request), agregar poda (p.ej. borrar entradas vencidas en cada
// llamada, o un limite de tamaño al Map) antes de depender de el.

const _inflight = new Map(); // key -> { promise, ts }
const DEFAULT_DEDUP_WINDOW_MS = 3000;

export function dedupFetch(url, options, windowMs = DEFAULT_DEDUP_WINDOW_MS) {
  const now = Date.now();
  const key = url;
  const existing = _inflight.get(key);
  if (existing && now - existing.ts < windowMs) {
    return existing.promise.then((res) => res.clone());
  }
  const promise = fetch(url, options);
  _inflight.set(key, { promise, ts: now });
  promise
    .then((res) => {
      _inflight.set(key, { promise: Promise.resolve(res.clone()), ts: Date.now() });
    })
    .catch(() => {
      _inflight.delete(key);
    });
  return promise.then((res) => res.clone());
}
